import DOMPurify from "dompurify";
import { resolveAssetUrl } from "@/lib/api";

/** TipTap 등에서 생성한 HTML을 공개 페이지에 안전하게 렌더링 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ["target", "rel"],
  });
}

/** HTML 본문 내 업로드 이미지 경로를 공개 페이지에서 접근 가능한 URL로 변환 */
export function resolveHtmlAssetUrls(html: string): string {
  return html.replace(
    /(<img[^>]*\ssrc=["'])(\/uploads\/[^"']+)(["'])/gi,
    (_, pre, path, post) => `${pre}${resolveAssetUrl(path)}${post}`,
  );
}

/** 에디터 표시용: 저장된 상대 경로를 절대 URL로 변환 */
export function prepareHtmlForEditor(html: string): string {
  if (!html) return "";
  return resolveHtmlAssetUrls(html);
}

/** 목록·검색용 plain text 추출 */
export function stripHtml(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

/** HTML 태그 포함 여부 (레거시 plain text 구분) */
export function isLikelyHtml(content: string): boolean {
  return /<[a-z][\s\S]*>/i.test(content);
}

const API_BASE = ((import.meta.env.VITE_API_BASE_URL as string) || "http://localhost:8080").replace(/\/$/, "");

/** 저장용: API 절대 URL을 /uploads 상대 경로로 정규화 (이미지만 있는 본문도 유지) */
export function normalizeEditorHtmlForStorage(html: string): string | null {
  const trimmed = html.trim();
  if (!trimmed) return null;
  const text = stripHtml(trimmed);
  const hasImage = /<img\b/i.test(trimmed);
  if (!text && !hasImage) return null;
  return trimmed.replaceAll(`${API_BASE}/uploads/`, "/uploads/");
}

/** @deprecated normalizeEditorHtmlForStorage 사용 */
export function normalizeEditorHtml(html: string): string | null {
  return normalizeEditorHtmlForStorage(html);
}

/** HTML 본문에서 첫 번째 img src 추출 */
export function extractFirstImageUrl(html: string): string | null {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const src = doc.querySelector("img")?.getAttribute("src")?.trim();
  if (src) return src;
  const match = html.match(/<img[^>]*\ssrc=["']([^"']+)["']/i);
  return match?.[1]?.trim() ?? null;
}

/** DB 저장용 image_url: API 절대 URL → /uploads 상대 경로 */
export function deriveImageUrlForStorage(
  imageUrl: string | null | undefined,
  content: string | null,
): string | null {
  const raw = (imageUrl?.trim() || extractFirstImageUrl(content || "")) ?? "";
  if (!raw) return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    const normalized = raw.replaceAll(`${API_BASE}/uploads/`, "/uploads/");
    return normalized.startsWith("/uploads/") ? normalized : raw;
  }
  return raw.startsWith("/") ? raw : null;
}

const PLACEHOLDER_THUMB = "https://placehold.co/600x400/eef2f7/2c4964?text=No+Image";

/** 목록·홈 썸네일: image_url 우선, 없으면 본문 첫 이미지, API 경로는 절대 URL로 변환 */
export function getPostThumbnailUrl(
  imageUrl: string | null | undefined,
  content?: string | null,
  fallback = PLACEHOLDER_THUMB,
): string {
  const raw = imageUrl?.trim() || extractFirstImageUrl(content || "") || "";
  const resolved = resolveAssetUrl(raw);
  return resolved || fallback;
}
